import React, { useEffect, useState } from 'react';
import { Typography, Divider, Badge, Row, Col, Card, Statistic } from 'antd';
import { ExclamationCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './Overview.css';

const { Title } = Typography;

const pastelColors = {
  BMS: '#FFB3BA',
  BPS: '#FFDFBA',
  PDB: '#FFFFBA',
  MPPT1: '#BAFFC9',
  MPPT2: '#BAE1FF',
  MC1: '#C9BAFF',
  MC2: '#FFCBA4',
  faults: '#FFBAD3'
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="label">{new Date(label).toLocaleTimeString()}</p>
        {payload.map((item) => (
          <p key={item.dataKey} style={{ color: item.stroke }}>{`${item.dataKey}: ${item.value}`}</p>
        ))}
      </div>
    );
  }

  return null;
};

const Overview = ({ data }) => {
  const [systemStatus, setSystemStatus] = useState({
    BMS: { count: 0, lastMessage: null, status: 'inactive', faults: 0 },
    BPS: { count: 0, lastMessage: null, status: 'inactive', faults: 0 },
    PDB: { count: 0, lastMessage: null, status: 'inactive', faults: 0 },
    MPPT1: { count: 0, lastMessage: null, status: 'inactive', faults: 0 },
    MPPT2: { count: 0, lastMessage: null, status: 'inactive', faults: 0 },
    MC1: { count: 0, lastMessage: null, status: 'inactive', faults: 0 },
    MC2: { count: 0, lastMessage: null, status: 'inactive', faults: 0 },
    faults: { count: 0 },
  });

  const [chartData, setChartData] = useState([]);

  const updateSystemStatus = () => {
    const currentTime = Date.now();
    const updatedStatus = { ...systemStatus };

    // Only update systems that have received new data
    Object.keys(data).forEach((key) => {
      const upperKey = key.toUpperCase();
      if (systemStatus[upperKey] && Object.keys(data[key]).length > 0) {
        updatedStatus[upperKey].count += 1;
        updatedStatus[upperKey].lastMessage = currentTime;
        updatedStatus[upperKey].status = 'active';
        if (upperKey === 'FAULTS') {
          updatedStatus[upperKey].faults += 1;
        }
      }
    });

    // Update inactive status based on the last message time
    Object.keys(systemStatus).forEach((key) => {
      if (systemStatus[key].lastMessage && currentTime - systemStatus[key].lastMessage > 10000) { // 10 seconds
        updatedStatus[key].status = 'inactive';
      }
    });

    setSystemStatus(updatedStatus);

    const newChartData = { timestamp: currentTime };
    Object.keys(systemStatus).forEach((key) => {
      newChartData[key] = updatedStatus[key].count;
    });

    setChartData((prevData) => [...prevData, newChartData].slice(-30)); // Keep the last 30 data points
  };

  useEffect(() => {
    const interval = setInterval(updateSystemStatus, 500);
    return () => clearInterval(interval);
  }, [data, systemStatus]);

  return (
    <div className="overview-container">
      <Title level={2} className="text-center">System Overview</Title>
      <Divider />
      <div className="chart-container">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="timestamp" tickFormatter={(tick) => new Date(tick).toLocaleTimeString()} />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {Object.keys(systemStatus).map((key) => (
              key !== 'faults' && <Line type="monotone" dataKey={key} stroke={pastelColors[key]} key={key} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <Divider />
      <Row gutter={[16, 16]}>
        {Object.keys(systemStatus).map((key) => (
          <Col span={8} key={key}>
            <Card className="system-card">
              <div className="card-header">
                <Title className="system-title" level={4}>{key}</Title>
                <Badge
                  status={systemStatus[key].status === 'active' ? 'success' : 'error'}
                  icon={
                    systemStatus[key].status === 'active' ? (
                      <CheckCircleOutlined />
                    ) : (
                      <ExclamationCircleOutlined />
                    )
                  }
                />
              </div>
              <Divider className="title-divider" />
              <Row>
                <Col span={11}>
                  <Statistic title="Messages" value={systemStatus[key].count} valueStyle={{ color: '#77dd77' }} />
                </Col>
                {key !== 'faults' && (
                  <>
                    <Divider type="vertical" />
                    <Col span={11}>
                      <Statistic title="Faults" value={systemStatus[key].faults} valueStyle={{ color: '#ff4d4f' }} />
                    </Col>
                  </>
                )}
              </Row>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default Overview;
